import { useId } from "react";
import { t } from "ttag";

import { useToast } from "metabase/common/hooks";
import { useUpdateMetabotMutation } from "metabase/metabot";
import { SettingHeader } from "metabase/settings-components";
import { Box, Flex, Switch, Text } from "metabase/ui";
import type { MetabotInfo } from "metabase-types/api";

/** Restricts a Metabot to verified and curated metrics and models. */
export function MetabotVerifiedContentConfigurationPane({
  metabot,
}: {
  metabot: MetabotInfo;
}) {
  const switchId = useId();
  const [updateMetabot, { isLoading }] = useUpdateMetabotMutation();
  const [sendToast] = useToast();
  const isOn = metabot.use_verified_content;

  const handleChange = async (useVerifiedContent: boolean) => {
    const result = await updateMetabot({
      id: metabot.id,
      use_verified_content: useVerifiedContent,
    });

    if (result.error) {
      sendToast({
        message: t`Couldn't update which content Metabot uses`,
        variant: "negative",
      });
    }
  };

  return (
    <Box>
      <SettingHeader
        id={switchId}
        title={t`Verified or curated content`}
        description={t`Metabot only uses metrics and models that are verified, in an Official collection, or published in the Library.`}
      />
      <Flex align="center" gap="lg" mt="lg">
        <Switch
          id={switchId}
          aria-label={t`Only use verified or curated content`}
          checked={isOn}
          disabled={isLoading}
          onChange={(event) => handleChange(event.target.checked)}
          w="auto"
        />
        <Text c={isOn ? "text-primary" : "text-secondary"} fw="500">
          {t`Only use verified or curated content`}
        </Text>
      </Flex>
    </Box>
  );
}
